// src/utils/alertRouting.js
//
// ONE routing map for alert "View" actions (K3P0DV7j). Both alert surfaces
// (the bell/hub in AlertsIndicator and the popup NotificationOverlay) had
// their own hand-maintained if/else chains that drifted — the popup knew
// three kinds, the bell knew eight, and several kinds (expense, scheduling,
// milestone slips) had no branch ANYWHERE despite carrying their deep-link
// ids, so View dumped them on comms or the customer page.
//
// ⚠ Field access: alertDelivery.js spreads `extra{}` FLAT onto the alert doc,
// so the canonical read is the FLAT field; `extra?.` is kept as a fallback
// for any older docs that stored it nested. Read via flat() only.
//
// Adding a new alert kind = one entry here. Every route returns a valid
// vue-router location; the fallback (customer page, else Communications) is
// baked in so callers can always `router.push(routeForAlert(alert))`.

import operationalAlertPolicy from '../../functions-shared/notifications/operationalAlertPolicy'
const flat = (alert, key) => alert?.[key] ?? alert?.extra?.[key]

const customerFallback = alert =>
  alert?.customerId
    ? { name: 'CustomerCommandCenter', params: { id: alert.customerId } }
    : { name: 'Communications' }

const commsResolution = alert => {
  const query = { openModal: 'comms' }
  const commId = flat(alert, 'commId')
  if (commId) query.highlightMsgId = commId
  const cType = flat(alert, 'commType')
  if (cType === 'email') query.mode = 'email'
  else if (cType === 'text') query.mode = 'text'
  else if (cType === 'phone' || cType === 'voicemail') query.mode = 'phone'
  return { name: 'CustomerCommandCenter', params: { id: alert.customerId }, query }
}

const calendarEvent = alert => {
  const query = {}
  if (flat(alert, 'googleEventId')) query.openEvent = flat(alert, 'googleEventId')
  if (flat(alert, 'serviceCaseId')) query.serviceCaseId = flat(alert, 'serviceCaseId')
  if (flat(alert, 'miscEventId')) query.miscEventId = flat(alert, 'miscEventId')
  return { path: '/calendar', query }
}

// ExpenseReportsPage already honors ?openReport=<id> (its own deep-link
// handler) — the alert ids were simply never sent there.
const phoneComm = alert => {
  if (!alert.customerId) return { name: 'Communications' }
  const query = { openModal: 'comms', mode: 'phone' }
  const commId = flat(alert, 'commId')
  if (commId) query.highlightMsgId = commId
  return { name: 'CustomerCommandCenter', params: { id: alert.customerId }, query }
}

const noteDeepLink = alert => {
  if (!alert.customerId) return { name: 'Communications', query: { tab: 'notes' } }
  const query = { openModal: 'notes' }
  const noteId = flat(alert, 'noteId')
  if (noteId) query.highlightMsgId = noteId
  const stage = flat(alert, 'stage')
  if (stage && stage !== 'General') query.stage = stage
  return { name: 'CustomerCommandCenter', params: { id: alert.customerId }, query }
}

// A sender we could not match has no customer by definition: open Communications on the Unknown tab with that
// record selected (CommunicationsPage reads ?tab=unknown&unknownId=). The id travels as unknownInboundId, or only
// inside unknownInboundPath ('unknownInbound/{id}') on voicemail alerts. No record named (the AI customer line's
// new-lead voicemail carries a customerId instead) keeps the old landing, the Communications page.
const unknownInboundRecord = alert => {
  const path = flat(alert, 'unknownInboundPath')
  const fromPath =
    typeof path === 'string' && path.startsWith('unknownInbound/') ? path.split('/')[1] || '' : ''
  const id = flat(alert, 'unknownInboundId') || fromPath
  return id
    ? { name: 'Communications', query: { tab: 'unknown', unknownId: id } }
    : { name: 'Communications' }
}

const expenseReport = alert => {
  const id = flat(alert, 'expenseReportId')
  return { name: 'ExpenseReports', query: id ? { openReport: id } : {} }
}

const internalLink = alert => {
  const link = flat(alert, 'href') || flat(alert, 'link')
  return typeof link === 'string' && /^\/(?![\\/])/.test(link) && !/[\r\n]/.test(link)
    ? { path: link }
    : customerFallback(alert)
}

// The blog's approval flow: Settings → Consumer Site → Blog, the approval queue above the posts.
const blogApprovals = () => ({ name: 'Settings', query: { tab: 'consumer-site', section: 'blog' } })

const KIND_ROUTES = {
  mretsAutomation: alert => ({
    name: 'SiteMonitoring',
    query: { mrets: '1', ...(flat(alert, 'siteId') ? { siteId: flat(alert, 'siteId') } : {}) },
  }),
  mretsRunSummary: () => ({ name: 'SiteMonitoring', query: { mrets: '1' } }),
  // The quiet switches in how sites are judged: meters by their counters, expected production aged with the panels.
  siteMonitoringMeterSwitch: () => ({ name: 'SiteMonitoring' }),
  // The standing M-RETs ledger check: M-RETs and the CRM's filing history disagree.
  mretsLedgerDivergence: () => ({ name: 'SiteMonitoring', query: { mrets: '1' } }),
  mretsOverrideStale: alert => ({
    name: 'SiteMonitoring',
    query: { mrets: '1', ...(flat(alert, 'siteId') ? { siteId: flat(alert, 'siteId') } : {}) },
  }),
  mretsOverrideResponseReceived: alert => ({
    name: 'SiteMonitoring',
    query: { mrets: '1', ...(flat(alert, 'siteId') ? { siteId: flat(alert, 'siteId') } : {}) },
  }),
  phoneSystemHealth: () => ({ name: 'SystemMonitoring' }),
  buildPartnerJobArrived: customerFallback,
  nightlyGuardFailure: () => ({ name: 'SystemMonitoring' }),
  aiAuthorRequest: internalLink,
  siteAnalysisRequest: internalLink,
  // Aurora's request on a paid campaign: its page, where it waits for a person
  paidCampaignApproval: internalLink,
  blogDraftSubmitted: blogApprovals,
  blogDraftPublished: blogApprovals,
  blogDraftReturned: blogApprovals,
  // An AI teammate's draft (docs/features/aurora_inline_drafts_FEATURE.md): the thread, the message it answers
  // highlighted. On a phone AlertOpenRedirect takes the mobile route (mobileAlertRoute.js) instead.
  aiDraftRequest: commsResolution,
  aiDraftReady: commsResolution,
  emergencyCommunication: commsResolution,
  systemMonitoring: alert => ({
    name: 'SystemMonitoring',
    query: { job: flat(alert, 'monitorJobId') },
  }),
  leadPool: () => ({ name: 'LeadPool' }),
  calendarEventAccepted: calendarEvent,
  calendarEventDeclined: calendarEvent,
  calendarEventTentative: calendarEvent,
  inventoryLowStock: () => ({ name: 'InventoryDashboard' }),
  inventoryCritical: () => ({ name: 'InventoryDashboard' }),
  kittingProblem: alert => {
    const query = {}
    const koid = flat(alert, 'kittingOrderId')
    if (koid) query.koid = koid
    else if (alert.customerId) query.customerId = alert.customerId
    return { name: 'KittingSchedule', query }
  },
  bugReportSubmitted: alert => {
    const id = flat(alert, 'bugReportId')
    return { name: 'BugReports', query: id ? { id } : {} }
  },
  bugReportStatusChanged: alert => {
    const id = flat(alert, 'bugReportId')
    return { name: 'BugReports', query: id ? { id } : {} }
  },
  communicationResolutionNeeded: commsResolution,
  serviceCasesActive: () => ({ name: 'ServiceCases' }),
  // Notes land ON the note (Erik 2026-08-18: "view notes for Mildred took me
  // to comms, not THAT note"). The machinery already existed end-to-end —
  // the CCC notes modal honors highlightMsgId and MessageThread's
  // scrollToMessage resolves n-{id} — the alert's noteId was just never
  // routed into it.
  noteAll: noteDeepLink,
  noteTagged: noteDeepLink,
  // Expense lifecycle (previously routeless — the ids were delivered and
  // ignored; these carry no customerId so they always fell to Communications).
  expenseSubmitted: expenseReport,
  expenseApproved: expenseReport,
  expenseRejected: expenseReport,
  expenseAccountantNotice: expenseReport,
  // Phone family (2026-08-18 full-surface reconciliation): voicemails and
  // call alerts land in the comms modal in PHONE mode, highlighting the call
  // row when the alert carries its commId (voiceHandlers delivers it).
  // The unknown-sender kinds have no customer by definition → Communications,
  // Unknown tab, the record selected (unknownInboundRecord).
  voicemail: phoneComm,
  incomingCall: phoneComm,
  missedCall: phoneComm,
  missedCallBrowser: phoneComm,
  unknownVoicemail: unknownInboundRecord,
  unknownText: unknownInboundRecord,
  unknownEmail: unknownInboundRecord,
  // Unread-comm rollups are PER-CUSTOMER COUNTERS (userCommsUnread increments
  // unreadTextCount etc.) — no individual comm id exists to highlight. The
  // honest best landing is the comms modal in the right mode: the thread
  // opens at the newest message, which is the unread one.
  unreadText: alert =>
    alert.customerId
      ? {
          name: 'CustomerCommandCenter',
          params: { id: alert.customerId },
          query: { openModal: 'comms', mode: 'text' },
        }
      : { name: 'Communications' },
  unreadEmail: alert =>
    alert.customerId
      ? {
          name: 'CustomerCommandCenter',
          params: { id: alert.customerId },
          query: { openModal: 'comms', mode: 'email' },
        }
      : { name: 'Communications' },
  unreadChat: alert =>
    alert.customerId
      ? {
          name: 'CustomerCommandCenter',
          params: { id: alert.customerId },
          query: { openModal: 'comms', mode: 'all' },
        }
      : { name: 'Communications' },
  // Finance planner nightly check (docs/features/finance_planner_FEATURE.md M4): the scenarios it measured.
  financePlanChanged: () => ({ name: 'AdminFinance', query: { tab: 'scenarios' } }),
  financePlanSourceProblem: () => ({ name: 'AdminFinance', query: { tab: 'budget' } }),
  // Milestone slips deliver an internal link path — honor it.
  milestoneSlip: internalLink,
}

export function routeForAlert(alert) {
  if (!alert) return { name: 'Communications' }
  if (
    operationalAlertPolicy.SUPERADMIN_EVENTS.includes(alert.kind) &&
    alert.kind !== 'systemMonitoring'
  )
    return { name: 'SystemMonitoring' }
  const exact = KIND_ROUTES[alert.kind]
  if (exact) return exact(alert)
  // Prefix families: scheduling/workflow alerts live on the customer; AI
  // reports live on their own page.
  if (typeof alert.kind === 'string') {
    if (alert.kind.startsWith('workflow_')) return customerFallback(alert)
    if (alert.kind.startsWith('aiReport')) {
      const report = flat(alert, 'reportId')
      return { name: 'AiReports', ...(report ? { query: { report } } : {}) }
    }
  }
  return customerFallback(alert)
}
